import { useState } from 'react';
import { Button } from '@/components/ui/button';

import { TALENT_PHOTO_REASON_MAX_LENGTH } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';
import { useDeleteTalentMutation, useRemoveTalentPhotoMutation } from '@/lib/mutations';

interface TalentRef {
  id: string;
  fullName: string;
}

/**
 * Deleting a talent: a compulsory reason, then the call. Shared by the list and the
 * detail screen, so the confirmation an admin learns in one place is the one they meet
 * in the other. What happens afterwards is the caller's — the list closes the panel,
 * the detail leaves a page whose record no longer exists.
 */
export function DeleteTalentPanel({
  talent,
  onCancel,
  onDeleted,
}: {
  talent: TalentRef;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const t = useTranslations('admin.talents');
  const [reason, setReason] = useState('');

  const mutation = useDeleteTalentMutation({
    okToast: t('delete.done'),
    failToast: t('delete.failed'),
    onSuccess: onDeleted,
  });

  return (
    <div className="space-y-2 rounded-2xl border border-rose-200 bg-rose-50/60 p-4">
      <p className="text-[13px] font-bold text-navy">{t('delete.heading')}</p>
      <p className="text-[12px] text-conc-500">{t('delete.notice')}</p>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('delete.label')}
        aria-label={t('delete.label')}
        className="w-full rounded-lg border border-conc-100 p-2 text-xs"
      />
      <div className="flex gap-2">
        <Button
          type="button"
          variant="danger"
          size="xs"
          disabled={!reason.trim() || mutation.isPending}
          onClick={() => mutation.mutate({ id: talent.id, reason: reason.trim() })}
        >
          {t('delete.confirm')}
        </Button>
        <Button type="button" variant="outline" size="xs" onClick={onCancel}>
          {t('delete.cancel')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Taking a talent's photo down. The reason is compulsory: it goes to the audit log and
 * to the talent, who is told why the photo went rather than finding it gone.
 */
export function RemovePhotoPanel({
  talent,
  onCancel,
  onRemoved,
}: {
  talent: TalentRef;
  onCancel: () => void;
  onRemoved: () => void;
}) {
  const t = useTranslations('admin.talents');
  const [reason, setReason] = useState('');

  const mutation = useRemoveTalentPhotoMutation({
    okToast: t('photo.done'),
    failToast: t('photo.failed'),
    onSuccess: onRemoved,
  });

  return (
    <div
      role="group"
      aria-label={t('photo.heading')}
      className="space-y-2 rounded-2xl border border-conc-200 bg-sideline p-4"
    >
      <p className="text-[13px] font-bold text-navy">
        {t('photo.heading')}
        <span className="ms-1.5 font-normal text-conc-500">· {talent.fullName}</span>
      </p>
      <p className="text-[12px] text-conc-500">{t('photo.notice')}</p>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('photo.label')}
        aria-label={t('photo.label')}
        maxLength={TALENT_PHOTO_REASON_MAX_LENGTH}
        className="w-full rounded-lg border border-conc-100 bg-white p-2 text-xs"
      />
      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          size="xs"
          disabled={!reason.trim() || mutation.isPending}
          onClick={() => mutation.mutate({ id: talent.id, reason: reason.trim() })}
        >
          {t('photo.confirm')}
        </Button>
        <Button type="button" variant="outline" size="xs" onClick={onCancel}>
          {t('photo.cancel')}
        </Button>
      </div>
    </div>
  );
}
